import React, { useState, useEffect } from 'react';
import { Check, Star, CheckCircle, XCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { billingAPI, authAPI } from '../api/client';

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  popular?: boolean;
  currentPlan?: boolean;
}

export function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cancelled') === 'true' || params.get('stripe') === 'cancel') {
      setNotification({ type: 'error', message: 'Checkout cancelled.' });
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setNotification(null), 5000);
    }
  }, []);

  const plans: PricingPlan[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      period: 'forever',
      description: 'Perfect for getting started with budget tracking',
      features: [
        'Up to 50 transactions per month',
        'Basic expense categorization',
        'Simple budget tracking',
        'Export to CSV',
        'Email support'
      ],
      currentPlan: true
    },
    {
      id: 'premium',
      name: 'Premium',
      price: billingCycle === 'monthly' ? 15.00 : 10.50,
      period: billingCycle === 'monthly' ? 'month' : 'month (billed yearly)',
      description: 'Advanced features for serious budget management',
      features: [
        'Unlimited transactions',
        'AI-powered expense categorization',
        'Advanced analytics and insights',
        'Custom categories and budgets',
        'Bank integration',
        'Receipt scanning with OCR',
        'Priority support',
        'Export to multiple formats'
      ],
      popular: true
    }
  ];

  const formatPrice = (price: number, period: string) => {
    if (price === 0) return 'Free';
    return `$${price.toFixed(2)}/${period}`;
  };

  const handleUpgrade = async (planId: string) => {
    if (planId === 'premium') {
      setIsProcessing('checkout');
      
      try {
        // First get the user's household using the proper API client
        const authResponse = await authAPI.getUser();
        
        if (!authResponse.success || !authResponse.data) {
          throw new Error('Authentication required');
        }

        const { households } = authResponse.data;
        
        if (!households || households.length === 0) {
          throw new Error('No household found');
        }

        const response = await billingAPI.createCheckoutSession({
          planId: 'premium',
          householdId: households[0].id,
          billingCycle
        });

        if (response.success && response.data) {
          // Redirect to Stripe checkout
          window.location.href = response.data.checkoutUrl;
        } else {
          throw new Error(response.error || 'Failed to create checkout session');
        }
      } catch (error) {
        console.error('Error initiating checkout:', error);
        setIsProcessing(null);
        // You could show a toast notification here
        alert(error instanceof Error ? error.message : 'Failed to initiate checkout');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {notification && (
          <div className={`mb-8 p-4 rounded-md max-w-4xl mx-auto ${
            notification.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
          }`}>
            <div className="flex items-center justify-center">
              {notification.type === 'success' ? (
                <CheckCircle className="h-5 w-5 mr-2" />
              ) : (
                <XCircle className="h-5 w-5 mr-2" />
              )}
              {notification.message}
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Start free and upgrade when you need more advanced features
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-1 inline-flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors relative ${
                billingCycle === 'yearly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white dark:bg-gray-800 rounded-lg border-2 shadow-lg p-8 ${
                plan.popular
                  ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-900'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center shadow-lg">
                    <Star className="h-4 w-4 mr-1" />
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">{plan.description}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    {plan.price === 0 ? 'Free' : `$${plan.price.toFixed(2)}`}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-gray-600 dark:text-gray-400">
                      /{plan.period}
                    </span>
                  )}
                </div>
                {plan.id === 'premium' && billingCycle === 'yearly' && (
                  <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                    Save $54/year with yearly billing
                  </p>
                )}
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={plan.currentPlan || isProcessing === 'checkout'}
                className={`w-full py-3 px-6 rounded-md text-sm font-medium transition-colors ${
                  plan.currentPlan
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-900 dark:bg-gray-700 text-white hover:bg-gray-800 dark:hover:bg-gray-600'
                }`}
              >
                {plan.currentPlan ? (
                  'Current Plan'
                ) : isProcessing === 'checkout' ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  'Get Started'
                )}
              </button>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Can I change my plan anytime?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, 
                and we'll prorate the billing accordingly.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                We accept all major credit cards through Stripe, including Visa, MasterCard, American Express, 
                and Discover.
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Is there a free trial?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Our Free plan gives you full access to basic features. You can upgrade to Premium anytime 
                when you need more advanced features.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Have questions about our pricing?
          </p>
          <a
            href="mailto:support@autobudgetai.com"
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Contact our support team
          </a>
        </div>
      </div>
    </div>
  );
}